/**
 * End terrain generator (worldgen for DimensionId 'end').
 *
 * Vanilla coordinates, unscaled: a lens-shaped end stone main island around the
 * origin (top at y 62, radius ~76), ten obsidian pillars on a radius-42 ring
 * with a bedrock block and fire where the crystal sits, the (inactive) exit
 * portal fountain at the origin, and void everywhere else — except small outer
 * islands of end stone beyond ~1000 blocks. The 5x5 obsidian arrival platform
 * lives at END_SPAWN, 24+ blocks off the island like vanilla; it is built on
 * entry (buildSpawnPlatform), not by worldgen.
 *
 * Every pass is a pure function of (seed, x, y, z), so chunks generate
 * independently. Saved edits are applied on top by the chunk worker, which is
 * how the active exit portal (buildExitPortal(setBlock, true)) persists.
 */

import { CHUNK_SIZE, WORLD_HEIGHT } from '../constants';
import { AIR, BEDROCK_ID, CHEST_FACINGS, CHORUS_FLOWER_ID, CHORUS_PLANT_ID, END_GATEWAY_ID, END_STONE_BRICKS_ID, GLASS_ID, IRON_BARS_ID, PURPUR_BLOCK_ID, PURPUR_PILLAR_ID, DRAGON_EGG_ID, END_PORTAL_ID, END_STONE_ID, FIRE_ID, OBSIDIAN_ID, TORCH_ID, TORCH_WALLS } from '../blocks';
import { hash2, Simplex } from '../noise';
import type { LootKind } from '../game/loot';

export type SetBlock = (x: number, y: number, z: number, id: number) => void;

/** Surface (top solid block) of the main island at its centre. */
export const END_TOP = 62;
export const MAIN_ISLAND_RADIUS = 76;
/** Outer islands exist only farther than this from the origin. */
export const OUTER_ISLAND_MIN_DIST = 1000;

export interface EndPillar { x: number; z: number; height: number; radius: number; caged: boolean }

/**
 * Vanilla SpikeFeature: ten spikes at floor(42 cos/sin(2 pi i / 10)), spike i
 * having size s = SIZES[i] -> radius 2 + s/3 (integer), top y 76 + 3s, caged when
 * s is 1 or 2 (the cage itself is not built: this engine has no iron bars).
 * Vanilla shuffles the sizes per world; a fixed shuffle keeps this a constant.
 * The bedrock block is at (x, height, z) and fire (the crystal's spot) above it.
 */
const SIZES = [7, 2, 5, 9, 0, 4, 8, 1, 6, 3];
export const END_PILLARS: readonly EndPillar[] = SIZES.map((s, i) => ({
  x: Math.floor(42 * Math.cos((2 * Math.PI * i) / 10)),
  z: Math.floor(42 * Math.sin((2 * Math.PI * i) / 10)),
  height: 76 + s * 3,
  radius: 2 + Math.floor(s / 3),
  caged: s === 1 || s === 2,
}));
/** Lowest y a pillar is drawn from (it hangs a little below the island's underside). */
const PILLAR_FLOOR = 30;

/** Obsidian platform: 5x5 at y = 48, the player stands on it at END_SPAWN.y = 49. */
export const END_SPAWN = { x: 100, y: 49, z: 0 };

/** Where the exit portal's blocks sit (portal plane y); the fountain stem rises from it. */
export const END_EXIT_PORTAL = { x: 0, y: END_TOP + 1, z: 0 };

/** Dragon egg position on top of the fountain stem. */
export const END_EGG_POS = { x: 0, y: END_EXIT_PORTAL.y + 4, z: 0 };

/**
 * Vanilla EndPodiumFeature: bedrock disc (radius 3.5) under the portal plane, a
 * bedrock rim at the plane around a radius-2.5 pool (air, or END_PORTAL when
 * `active`), a 4-tall bedrock stem with four wall torches, and the dragon egg on
 * top when active.
 */
export function buildExitPortal(setBlock: SetBlock, active: boolean): void {
  const { x: ox, y: oy, z: oz } = END_EXIT_PORTAL;
  for (let y = oy - 1; y <= oy + 5; y++) {
    for (let dz = -4; dz <= 4; dz++) {
      for (let dx = -4; dx <= 4; dx++) {
        const d = Math.sqrt(dx * dx + dz * dz);
        if (d > 3.5) continue;
        let id: number;
        if (y < oy) id = BEDROCK_ID;
        else if (y > oy) id = AIR;
        else id = d > 2.5 ? BEDROCK_ID : active ? END_PORTAL_ID : AIR;
        setBlock(ox + dx, y, oz + dz, id);
      }
    }
  }
  for (let k = 0; k < 4; k++) setBlock(ox, oy + k, oz, BEDROCK_ID);
  const walls = TORCH_WALLS.get(TORCH_ID);
  if (walls) {
    // a wall torch is keyed by the face it sticks to: +X, -X, +Z, -Z
    setBlock(ox + 1, oy + 2, oz, walls[1]); setBlock(ox - 1, oy + 2, oz, walls[0]);
    setBlock(ox, oy + 2, oz + 1, walls[5]); setBlock(ox, oy + 2, oz - 1, walls[4]);
  }
  setBlock(END_EGG_POS.x, END_EGG_POS.y, END_EGG_POS.z, active ? DRAGON_EGG_ID : AIR);
}

/** The arrival platform: a 5x5 obsidian slab one block under END_SPAWN with 3 air above. */
export function buildSpawnPlatform(setBlock: SetBlock, cx = END_SPAWN.x, cy = END_SPAWN.y, cz = END_SPAWN.z): void {
  for (let dz = -2; dz <= 2; dz++) {
    for (let dx = -2; dx <= 2; dx++) {
      setBlock(cx + dx, cy - 1, cz + dz, OBSIDIAN_ID);
      for (let dy = 0; dy < 3; dy++) setBlock(cx + dx, cy + dy, cz + dz, AIR);
    }
  }
}

/**
 * Vanilla SpikeFeature cage: a 5x5 iron-bars box round the crystal, four layers
 * tall (the top layer is a roof), standing on the pillar's top face. The crystal
 * and its fire stay inside it.
 */
export function buildCage(setBlock: SetBlock, p: EndPillar): void {
  for (let dy = 0; dy <= 3; dy++) {
    for (let dz = -2; dz <= 2; dz++) {
      for (let dx = -2; dx <= 2; dx++) {
        if (Math.abs(dx) === 2 || Math.abs(dz) === 2 || dy === 3) setBlock(p.x + dx, p.height + 1 + dy, p.z + dz, IRON_BARS_ID);
      }
    }
  }
}


// --- outer islands, end cities, gateways -------------------------------------------

export const OUTER_CELL = 192;

export interface OuterIsland { x: number; z: number; R: number; top: number }

/** The island of outer cell (i, j), or null (pure in the seed). */
export function outerIsland(seed: number, i: number, j: number): OuterIsland | null {
  const ix = i * OUTER_CELL + 40 + hash2(i, j, seed ^ 0x11) * (OUTER_CELL - 80);
  const iz = j * OUTER_CELL + 40 + hash2(i, j, seed ^ 0x22) * (OUTER_CELL - 80);
  if (Math.hypot(ix, iz) < OUTER_ISLAND_MIN_DIST || hash2(i, j, seed ^ 0x33) < 0.4) return null;
  return {
    x: ix, z: iz, R: 14 + hash2(i, j, seed ^ 0x44) * 38,
    top: Math.round(52 + hash2(i, j, seed ^ 0x55) * 26),
  };
}

/** An end city on an island: the main tower at (x, top, z), a bridge east to a second tower, and sometimes a ship. */
export interface EndCity { cx: number; cz: number; y: number; ship: boolean; i: number; j: number; seed: number }
const CITY_REACH = 40;

export function endCityAt(seed: number, i: number, j: number): EndCity | null {
  const isl = outerIsland(seed, i, j);
  if (!isl || isl.R < 28 || hash2(i, j, seed ^ 0xc17) >= 0.55) return null;
  return { cx: Math.floor(isl.x), cz: Math.floor(isl.z), y: isl.top, ship: hash2(i, j, seed ^ 0x5819) < 0.45, i, j, seed };
}

function cityNear(seed: number, ci: number, cj: number, x: number, z: number, margin: number): boolean {
  for (let j = cj - 1; j <= cj + 1; j++) {
    for (let i = ci - 1; i <= ci + 1; i++) {
      const c = endCityAt(seed, i, j);
      if (c && x >= c.cx - 6 - margin && x <= c.cx + 36 + margin && z >= c.cz - 6 - margin && z <= c.cz + 6 + margin) return true;
    }
  }
  return false;
}

/** Chests of a city: [x, y, z, loot]. The tower ones are `end_city`, the ship's is `end_ship` (it holds the elytra). */
export function endCityChests(c: EndCity): Array<[number, number, number, LootKind]> {
  const out: Array<[number, number, number, LootKind]> = [
    [c.cx + 2, c.y + 1, c.cz + 2, 'end_city'], [c.cx - 2, c.y + 9, c.cz - 2, 'end_city'],
    [c.cx + 17, c.y + 9, c.cz, 'end_city'],
  ];
  if (c.ship) out.push([c.cx + 30, c.y + 9, c.cz, 'end_ship']);
  return out;
}

/** Loot table for an End chest at a position (cities and ships), else the default table. */
export function endLootKind(seed: number, x: number, y: number, z: number): LootKind {
  const ci = Math.floor(x / OUTER_CELL), cj = Math.floor(z / OUTER_CELL);
  for (let j = cj - 1; j <= cj + 1; j++) {
    for (let i = ci - 1; i <= ci + 1; i++) {
      const city = endCityAt(seed, i, j);
      const hit = city && endCityChests(city).find((k) => k[0] === x && k[1] === y && k[2] === z);
      if (hit) return hit[3];
    }
  }
  return 'default';
}

function fillClipped(put: SetBlock, ox: number, oz: number, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, id: number): void {
  const xa = Math.max(x0, ox), xb = Math.min(x1, ox + CHUNK_SIZE - 1), za = Math.max(z0, oz), zb = Math.min(z1, oz + CHUNK_SIZE - 1);
  for (let z = za; z <= zb; z++) for (let x = xa; x <= xb; x++) for (let y = y0; y <= y1; y++) put(x, y, z, id);
}

/** Draw the part of a city that falls inside chunk (ox, oz). Pure: every chunk redraws it identically. */
function buildEndCity(c: EndCity, ox: number, oz: number, put: SetBlock): void {
  if (ox > c.cx + 36 + 1 || ox + CHUNK_SIZE < c.cx - 6 - 1 || oz > c.cz + 7 || oz + CHUNK_SIZE < c.cz - 7) return;
  const f = (x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, id: number): void =>
    fillClipped(put, ox, oz, c.cx + x0, c.y + y0, c.cz + z0, c.cx + x1, c.y + y1, c.cz + z1, id);
  const at = (x: number, y: number, z: number, id: number): void => f(x, y, z, x, y, z, id);
  const hollowTower = (r: number, h: number, ux: number): void => {
    f(ux - r, 0, -r, ux + r, 0, r, END_STONE_BRICKS_ID);
    f(ux - r, 1, -r, ux + r, h, r, PURPUR_BLOCK_ID);
    f(ux - r + 1, 1, -r + 1, ux + r - 1, h - 1, r - 1, AIR);
    for (const sx of [-r, r]) for (const sz of [-r, r]) f(ux + sx, 1, sz, ux + sx, h, sz, PURPUR_PILLAR_ID);
    f(ux - r, h + 1, -r, ux + r, h + 1, r, END_STONE_BRICKS_ID);
    for (const sx of [-r, r]) for (const sz of [-r, r]) at(ux + sx, h + 2, sz, PURPUR_PILLAR_ID);
    // windows
    for (let w = -r + 2; w <= r - 2; w += 2) {
      for (const [dx, dz] of [[w, -r], [w, r], [-r, w], [r, w]]) f(ux + dx, 3, dz, ux + dx, 4, dz, GLASS_ID);
    }
  };
  // main tower: 9x9, 16 tall, a floor at 8 reached by a ring of single steps, doors south
  hollowTower(4, 16, 0);
  f(-3, 8, -3, 3, 8, 3, END_STONE_BRICKS_ID);
  const ring: Array<[number, number]> = [];
  for (let u = -3; u <= 3; u++) ring.push([u, -3]);
  for (let v = -2; v <= 3; v++) ring.push([3, v]);
  for (let k = 0; k < 8; k++) { const [u, v] = ring[k]; f(u, 1, v, u, 1 + k, v, PURPUR_BLOCK_ID); }
  f(-1, 1, 4, 1, 3, 4, AIR);   // doorway (+z)
  f(4, 9, -1, 4, 11, 1, AIR);  // opening east on the upper floor, onto the bridge
  f(4, 1, -1, 4, 3, 1, AIR);
  put3(put, ox, oz, c, 2, 1, 2, 2);
  put3(put, ox, oz, c, -2, 9, -2, 0);
  // bridge east at floor level 8, then the second tower at x 17
  f(5, 8, -1, 14, 8, 1, END_STONE_BRICKS_ID);
  for (let x = 5; x <= 14; x += 2) { at(x, 9, -1, PURPUR_PILLAR_ID); at(x, 9, 1, PURPUR_PILLAR_ID); }
  hollowTower(3, 12, 17);
  f(14, 9, -1, 14, 11, 1, AIR);
  f(15, 8, -1, 19, 8, 1, END_STONE_BRICKS_ID);
  put3(put, ox, oz, c, 17, 9, 0, 1);
  if (!c.ship) return;
  // the ship: bridge on east from the second tower, a hull of purpur 5 wide, a mast, the elytra chest aft
  f(20, 9, -1, 20, 11, 1, AIR);
  f(21, 8, -1, 24, 8, 1, END_STONE_BRICKS_ID);
  f(24, 6, -2, 36, 7, 2, PURPUR_BLOCK_ID);
  f(25, 8, -2, 35, 8, 2, END_STONE_BRICKS_ID);
  f(24, 9, -2, 36, 9, -2, PURPUR_BLOCK_ID); f(24, 9, 2, 36, 9, 2, PURPUR_BLOCK_ID);
  f(25, 9, -1, 35, 12, 1, AIR);
  f(28, 9, 0, 28, 14, 0, PURPUR_PILLAR_ID);
  f(26, 8, 0, 26, 8, 0, END_STONE_BRICKS_ID);
  put3(put, ox, oz, c, 30, 9, 0, 1);
}

/** A chest at city-relative (u, y, v) facing `facing`, clipped to the chunk at (ox, oz). */
function put3(put: SetBlock, ox: number, oz: number, c: EndCity, u: number, y: number, v: number, facing: 0 | 1 | 2 | 3): void {
  const x = c.cx + u, z = c.cz + v;
  if (x < ox || x >= ox + CHUNK_SIZE || z < oz || z >= oz + CHUNK_SIZE) return;
  put(x, c.y + y, z, CHEST_FACINGS[facing]);
}

const GATEWAYS = 20;
export const END_GATEWAY_COUNT = GATEWAYS;
/** Vanilla: twenty gateways on a radius-96 ring round the main island, 75 up, in the void. */
export function endGatewayPos(index: number): { x: number; y: number; z: number } {
  const a = (2 * Math.PI * (((index % GATEWAYS) + GATEWAYS) % GATEWAYS)) / GATEWAYS;
  return { x: Math.floor(96 * Math.cos(a)), y: 75, z: Math.floor(96 * Math.sin(a)) };
}

/**
 * Vanilla EndGatewayFeature, for the dragon fight to call after each kill: a
 * bedrock column (two above, two below) round an End Gateway block, with the
 * cells beside the gateway cleared.
 */
export function buildEndGateway(setBlock: SetBlock, index: number): void {
  const { x, y, z } = endGatewayPos(index);
  for (let dy = -2; dy <= 2; dy++) setBlock(x, y + dy, z, dy === 0 ? END_GATEWAY_ID : BEDROCK_ID);
}

/**
 * Where a gateway sends you: the outer island nearest 1150 blocks out along the
 * gateway's bearing; feet at the island surface (the end stone under the centre
 * is always solid). Pure in (seed, index), no terrain needed.
 */
export function endGatewayExit(seed: number, index: number): { x: number; y: number; z: number } {
  const a = (2 * Math.PI * (((index % GATEWAYS) + GATEWAYS) % GATEWAYS + 0.5)) / GATEWAYS;
  const tx = Math.cos(a) * 1150, tz = Math.sin(a) * 1150;
  const ci = Math.floor(tx / OUTER_CELL), cj = Math.floor(tz / OUTER_CELL);
  let best: OuterIsland | null = null, bestD = Infinity;
  for (let r = 1; r <= 6 && !best; r++) {
    for (let j = cj - r; j <= cj + r; j++) {
      for (let i = ci - r; i <= ci + r; i++) {
        const isl = outerIsland(seed, i, j);
        if (!isl) continue;
        const d = Math.hypot(isl.x - tx, isl.z - tz);
        if (d < bestD) { bestD = d; best = isl; }
      }
    }
  }
  const isl = best ?? { x: tx, z: tz, R: 20, top: 60 };
  const ci2 = Math.floor(isl.x / OUTER_CELL), cj2 = Math.floor(isl.z / OUTER_CELL);
  const city = endCityAt(seed, ci2, cj2);
  const x = Math.floor(city ? isl.x - 10 : isl.x);
  return { x, y: isl.top + 1, z: Math.floor(isl.z) };
}

export class EndGenerator {
  readonly seed: number;
  private readonly shape: Simplex;
  private readonly detail: Simplex;

  constructor(seed: number) {
    this.seed = seed | 0;
    const s = this.seed ^ 0x454e44; // 'END'
    this.shape = new Simplex(s + 1);
    this.detail = new Simplex(s + 2);
  }

  /** [lowest, highest] solid y of the main island's column, or null in the void. */
  private mainColumn(x: number, z: number): [number, number] | null {
    const r = Math.hypot(x, z);
    if (r >= MAIN_ISLAND_RADIUS * 1.2) return null;
    const u = r / MAIN_ISLAND_RADIUS - this.shape.noise2(x / 30, z / 30) * 0.13;
    if (u >= 1) return null;
    const uu = Math.max(0, u);
    const top = END_TOP - Math.round(7 * uu * uu * uu);
    const thick = Math.round(26 * Math.sqrt(1 - uu * uu) * (0.8 + 0.2 * this.detail.noise2(x / 7 - 70, z / 7)));
    return thick < 1 ? [top, top] : [top - thick, top];
  }

  /** Outer-island cells are 192 blocks wide with one jittered island each. */
  private outerColumn(x: number, z: number): [number, number] | null {
    const ci = Math.floor(x / OUTER_CELL), cj = Math.floor(z / OUTER_CELL);
    let best: [number, number] | null = null;
    for (let j = cj - 1; j <= cj + 1; j++) {
      for (let i = ci - 1; i <= ci + 1; i++) {
        const isl = outerIsland(this.seed, i, j);
        if (!isl) continue;
        const u = Math.hypot(x - isl.x, z - isl.z) / isl.R - this.shape.noise2(x / 18, z / 18) * 0.15;
        if (u >= 1) continue;
        const uu = Math.max(0, u);
        const top = isl.top - Math.round(4 * uu * uu * uu);
        const thick = Math.max(1, Math.round(isl.R * 0.5 * Math.sqrt(1 - uu * uu)));
        if (!best || top > best[1]) best = [top - thick, top];
      }
    }
    return best;
  }

  /** Chorus plants on every outer island, end cities (with ships) on the big ones. */
  private outerContent(ox: number, oz: number, put: SetBlock): void {
    // chorus: sparse plants of 1..4 stems topped by a flower, on the island surface
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const x = ox + lx, z = oz + lz;
        if (hash2(x, z, this.seed ^ 0xc0c0) >= 0.012) continue;
        const col = this.outerColumn(x, z);
        if (!col || Math.hypot(x, z) < OUTER_ISLAND_MIN_DIST - 20) continue;
        const ci = Math.floor(x / OUTER_CELL), cj = Math.floor(z / OUTER_CELL);
        if (cityNear(this.seed, ci, cj, x, z, 10)) continue;
        const h = 1 + Math.floor(hash2(x, z, this.seed ^ 0xc0c1) * 4);
        for (let k = 1; k <= h; k++) put(x, col[1] + k, z, CHORUS_PLANT_ID);
        put(x, col[1] + h + 1, z, CHORUS_FLOWER_ID);
      }
    }
    // cities
    const ci0 = Math.floor((ox - CITY_REACH) / OUTER_CELL), ci1 = Math.floor((ox + CHUNK_SIZE + CITY_REACH) / OUTER_CELL);
    const cj0 = Math.floor((oz - CITY_REACH) / OUTER_CELL), cj1 = Math.floor((oz + CHUNK_SIZE + CITY_REACH) / OUTER_CELL);
    for (let j = cj0; j <= cj1; j++) {
      for (let i = ci0; i <= ci1; i++) {
        const city = endCityAt(this.seed, i, j);
        if (city) buildEndCity(city, ox, oz, put);
      }
    }
  }

  generateChunkBlocks(cx: number, cz: number, out: Uint16Array): void {
    out.fill(AIR);
    const ox = cx * CHUNK_SIZE, oz = cz * CHUNK_SIZE;
    const far = Math.hypot(ox + 8, oz + 8) > OUTER_ISLAND_MIN_DIST - 220;
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const x = ox + lx, z = oz + lz;
        const col = this.mainColumn(x, z) ?? (far ? this.outerColumn(x, z) : null);
        if (!col) continue;
        for (let y = Math.max(1, col[0]); y <= col[1]; y++) out[lx + lz * CHUNK_SIZE + y * 256] = END_STONE_ID;
      }
    }
    const put = (x: number, y: number, z: number, id: number): void => {
      const lx = x - ox, lz = z - oz;
      if (lx < 0 || lx >= CHUNK_SIZE || lz < 0 || lz >= CHUNK_SIZE || y < 1 || y >= WORLD_HEIGHT) return;
      out[lx + lz * CHUNK_SIZE + y * 256] = id;
    };
    if (far) this.outerContent(ox, oz, put);
    for (const p of END_PILLARS) {
      if (p.x + p.radius < ox || p.x - p.radius >= ox + CHUNK_SIZE || p.z + p.radius < oz || p.z - p.radius >= oz + CHUNK_SIZE) continue;
      for (let dz = -p.radius; dz <= p.radius; dz++) {
        for (let dx = -p.radius; dx <= p.radius; dx++) {
          if (dx * dx + dz * dz > p.radius * p.radius + 1) continue;
          for (let y = PILLAR_FLOOR; y <= p.height; y++) put(p.x + dx, y, p.z + dz, OBSIDIAN_ID);
        }
      }
      put(p.x, p.height, p.z, BEDROCK_ID);
      put(p.x, p.height + 1, p.z, FIRE_ID);
      if (p.caged) buildCage(put, p);
    }
    if (Math.abs(ox + 8) < 24 && Math.abs(oz + 8) < 24) buildExitPortal(put, false);
  }
}
